/*
 * How AI chats' rows read in the activity feed and in a desktop banner, and where they lead (S13 §7).
 *
 * The server writes a row's payload (`ActivityData`, generated from `aichat/wire/activity.go`) as ids,
 * enums and the chat's key — never a title, never a word of Claude's or anybody's (the text rule,
 * S03 §3). The sentence is made here, where the reader's slice holds the titles of the chats they
 * own: a row names its chat by title and falls back to the key where the reader has none. A row that
 * reached somebody who does not own the chat (`notOwner`: a partial landing told to every developer,
 * a publish told to whoever deploys) names the key and leads nowhere. A Claude login's row is about
 * no chat at all: it reaches the login's owner alone and leads to their Settings → Claude logins.
 *
 * Two rules hold for every row. **Nothing is invented**: a payload that lacks
 * what its sentence names answers "", and core falls back to a row it can draw. And **one list of
 * kinds**: `AICHAT_ACTIVITY_KINDS` names the generated kind words once, in the Go list's order
 * (`ActivityKinds`), each kind has its label and its sentences in the
 * catalogue, and the eleven kinds that are worth a banner say so beside their sentence, so the feed
 * and the banner never say two different things about one row. The kind's label is the row's own
 * word too — the banner's title and the name of the row's read button, per card where a card says
 * more than its kind — and a row that names no person stands under Claude's mark.
 *
 * The file is reached from the entry chunk through the module object, so it imports types, the
 * address helpers, the translator and the slice's reader, and nothing that draws.
 */

import { formatDate, formatList } from "../../core/i18n/format.ts"
import type { ActivityDesktop, ActivityRowView } from "../../core/modules/registry.ts"
import type { IconName } from "../../core/shell/icons.tsx"
import { client } from "../../core/store/store.ts"
import { AI_CLAUDE_LOGIN_HREF, aiChangesHref, aiChatHref } from "../../routes/links.ts"
import { t, type PlainAichatKey } from "./i18n/t.ts"
import { aichatSlice } from "./sync/slice.ts"
import {
	ActivityAccess,
	ActivityAnswered,
	ActivityAssigned,
	ActivityAttention,
	ActivityFinished,
	ActivityLogin,
	ActivityMention,
	ActivityPartlyPublished,
	ActivityPaused,
	ActivityPublished,
	ActivityQuestion,
	ActivityReady,
	ActivityReviewRequested,
	ActivityStale,
	ActivityStopped,
	LoginNoticeD1,
	LoginNoticeD14,
	LoginNoticeExpired,
	LoginNoticeRefused,
	PauseCeiling,
	PauseDisk,
	PauseInterrupted,
	PauseLimit,
	PauseLogin,
	PauseTooLong,
	ReasonFailed,
	WaitChecksFailed,
	WaitNeedsIntegration,
	type ActivityData,
} from "./wire/aichat.gen.ts"
import { CardModify, CardPlan, ChatModify, ChoiceAllow, ChoiceKeep } from "./wire/aichatapi.gen.ts"

/** The kinds AI chats writes, in the Go list's order; each has `aichat.activity.<kind>.label`. */
export const AICHAT_ACTIVITY_KINDS = [
	ActivityQuestion,
	ActivityAnswered,
	ActivityFinished,
	ActivityStopped,
	ActivityPaused,
	ActivityLogin,
	ActivityReady,
	ActivityAttention,
	ActivityReviewRequested,
	ActivityPublished,
	ActivityPartlyPublished,
	ActivityAssigned,
	ActivityAccess,
	ActivityMention,
	ActivityStale,
] as const

export type AichatActivityKind = (typeof AICHAT_ACTIVITY_KINDS)[number]

/** Every kind of this module starts with it; the catalogue's keys leave it out. */
const KIND_PREFIX = "aichat."

/**
 * A row's payload as the server writes it. Every field is optional at the door: a row written by an
 * older release is still in somebody's feed.
 */
type AichatActivityData = Partial<ActivityData>

/** A row's payload as this module reads it; anything that is not an object reads as empty. */
function read(data: unknown): AichatActivityData {
	return typeof data === "object" && data !== null ? data : {}
}

/**
 * The chat's name as this reader knows it: the title from the slice, else the key. Empty only for a
 * payload that names no chat, which then draws no sentence.
 */
function chatName(item: AichatActivityData): string {
	const slice = aichatSlice(client.model)
	if (item.chatId !== undefined) {
		const title = slice.titles[item.chatId]
		if (title !== undefined && title !== "") return title
	}
	return item.key ?? ""
}

/**
 * Whether the row leads to the chat: not where it reached somebody who does not own it, whom a link
 * would only take to the not-found page.
 */
function reachable(item: AichatActivityData): boolean {
	return item.key !== undefined && item.key !== "" && item.notOwner !== true
}

/**
 * A login row's sentence by its notice: the reminders name the day the token ends, in the reader's
 * own time zone; a row without its label, or of a notice this build does not know, draws nothing.
 */
function loginLine(item: AichatActivityData): string {
	const label = item.label ?? ""
	if (label === "") return ""
	const date = item.expiresAt === undefined ? "" : formatDate(item.expiresAt)
	switch (item.state) {
		case LoginNoticeD14:
			return date === "" ? "" : t("aichat.activity.login.d14", { label, date })
		case LoginNoticeD1:
			return date === "" ? "" : t("aichat.activity.login.d1", { label, date })
		case LoginNoticeExpired:
			return t("aichat.activity.login.expired", { label })
		case LoginNoticeRefused:
			return t("aichat.activity.login.refused", { label })
		default:
			return ""
	}
}

/** The chat's page, where the reader may open it. */
function chatHref(
	item: AichatActivityData,
	search?: Record<string, string | number>,
): string | null {
	return reachable(item) && item.key !== undefined ? aiChatHref(item.key, search) : null
}

/** The chat's review, where the reader may open it. */
function changesHref(item: AichatActivityData): string | null {
	return reachable(item) && item.key !== undefined ? aiChangesHref(item.key) : null
}

/** The part names of a landing, as the configuration writes them, in the order the payload lists. */
function partNames(ids: readonly string[] | undefined): string {
	const parts = aichatSlice(client.model).setup?.parts ?? []
	return formatList((ids ?? []).map((id) => parts.find((part) => part.id === id)?.name ?? id))
}

/** The paused row's sentence per reason; a reason this build cannot name has none. */
const PAUSE_SENTENCES: Record<string, ((title: string) => string) | undefined> = {
	[PauseLimit]: (title) => t("aichat.activity.paused.limit", { title }),
	[PauseLogin]: (title) => t("aichat.activity.paused.login", { title }),
	[PauseInterrupted]: (title) => t("aichat.activity.paused.interrupted", { title }),
	[PauseTooLong]: (title) => t("aichat.activity.paused.too_long", { title }),
	[PauseCeiling]: (title) => t("aichat.activity.paused.ceiling", { title }),
	[PauseDisk]: (title) => t("aichat.activity.paused.disk", { title }),
	[ReasonFailed]: (title) => t("aichat.activity.paused.failed", { title }),
}

/** The kind's name without the module's prefix, as its catalogue keys spell it. */
function kindName(kind: AichatActivityKind): string {
	return kind.slice(KIND_PREFIX.length)
}

/** The kind's label (`aichat.activity.<name>.label`): the row's word for itself and its banner's title. */
function labelOf(kind: AichatActivityKind): () => string {
	return () => t(`aichat.activity.${kindName(kind)}.label` as PlainAichatKey)
}

/**
 * The label of a row whose kind says less than the row: a plan or a request to change code is not
 * "something Claude asks", and a request that failed is not a pause somebody settles.
 */
function questionLabel(data: unknown): string {
	const card = read(data).cardKind
	if (card === CardPlan) return t("aichat.activity.question.label.plan")
	if (card === CardModify) return t("aichat.activity.question.label.modify")
	return t("aichat.activity.question.label")
}

function pausedLabel(data: unknown): string {
	return read(data).reason === ReasonFailed
		? t("aichat.activity.paused.label.failed")
		: t("aichat.activity.paused.label")
}

/** A banner per chat: twenty rows about one chat are one banner that updates. */
function tagOf(kind: AichatActivityKind): (data: unknown) => string {
	return (data) => `activity:${kind}:${String(read(data).chatId ?? 0)}`
}

/** The desktop banner of a kind that is worth one (S13 §7's banner column), titled by the row's label. */
function banner(
	kind: AichatActivityKind,
	title: (data: unknown) => string = labelOf(kind),
	onRaise = false,
): ActivityDesktop {
	return { title, tag: tagOf(kind), ...(onRaise ? { onRaise } : {}) }
}

/**
 * What every AI chat row stands under when it names no person: Claude asked, Claude finished, the
 * checks found something. The rows a person caused — an answer, a stop, a publish — draw that person.
 */
const CLAUDE_FACE: IconName = "claude"

/** The first actor's name, or "" when the row names nobody the feed can resolve. */
function actor(actorIds: readonly number[], nameOf: (id: number) => string): string {
	return actorIds[0] === undefined ? "" : nameOf(actorIds[0])
}

/** Each kind's sentence, link and banner; its label and face are added below, where a kind has none. */
const ROWS: Record<AichatActivityKind, ActivityRowView> = {
	/*
	 * Claude asks something the reader has to answer: a question, a plan to start, or a request to
	 * change code. The asker hears it at once; an assignee five minutes later, if it is still open.
	 */
	[ActivityQuestion]: {
		line: (data) => {
			const item = read(data)
			const title = chatName(item)
			if (title === "") return ""
			if (item.cardKind === CardPlan) return t("aichat.activity.question.plan", { title })
			if (item.cardKind === CardModify) return t("aichat.activity.question.modify", { title })
			return t("aichat.activity.question.sentence", { title })
		},
		href: (data) => chatHref(read(data)),
		label: questionLabel,
		desktop: banner(ActivityQuestion, questionLabel),
	},
	/*
	 * Somebody else answered the reader's card, or nobody did in time and Claude went on. For
	 * Claude's request to change code the answer is the news: allowed, or kept read only.
	 */
	[ActivityAnswered]: {
		line: (data, actorIds, nameOf) => {
			const item = read(data)
			const title = chatName(item)
			if (title === "") return ""
			const name = actor(actorIds, nameOf)
			if (name === "") return t("aichat.activity.answered.defaulted", { title })
			if (item.reason === ChoiceAllow) return t("aichat.activity.answered.allowed", { name, title })
			if (item.reason === ChoiceKeep) return t("aichat.activity.answered.kept", { name, title })
			return t("aichat.activity.answered.sentence", { name, title })
		},
		href: (data) => chatHref(read(data)),
	},
	/* The reader's request is done: answered in a read-only chat, finished in one that changes code. */
	[ActivityFinished]: {
		line: (data) => {
			const item = read(data)
			const title = chatName(item)
			if (title === "") return ""
			return item.mode === ChatModify
				? t("aichat.activity.finished.modify", { title })
				: t("aichat.activity.finished.read", { title })
		},
		href: (data) => chatHref(read(data)),
		desktop: banner(ActivityFinished),
	},
	/* Another owner stopped the reader's request. */
	[ActivityStopped]: {
		line: (data, actorIds, nameOf) => {
			const title = chatName(read(data))
			const name = actor(actorIds, nameOf)
			return title === "" || name === ""
				? ""
				: t("aichat.activity.stopped.sentence", { name, title })
		},
		href: (data) => chatHref(read(data)),
		desktop: banner(ActivityStopped),
	},
	/* The chat stopped for something a person or an admin has to settle. */
	[ActivityPaused]: {
		line: (data) => {
			const item = read(data)
			const title = chatName(item)
			const say = item.reason === undefined ? undefined : PAUSE_SENTENCES[item.reason]
			return title === "" || say === undefined ? "" : say(title)
		},
		href: (data) => chatHref(read(data)),
		label: pausedLabel,
		desktop: banner(ActivityPaused, pausedLabel),
	},
	/*
	 * One of the reader's own Claude logins (S17 §2), told to its owner alone: 14 days and 1 day
	 * before its token expires, at the expiry, and when Claude refused it. Each notice is its own row
	 * and its own banner — two logins' reminders never fold into one — and every one leads to the page
	 * where a new token is entered.
	 */
	[ActivityLogin]: {
		line: (data) => loginLine(read(data)),
		href: () => AI_CLAUDE_LOGIN_HREF,
		desktop: {
			title: labelOf(ActivityLogin),
			tag: (data) => {
				const item = read(data)
				return `activity:${ActivityLogin}:${item.login ?? ""}:${item.state ?? ""}`
			},
		},
	},
	/* A revision passed its checks and can be published. */
	[ActivityReady]: {
		line: (data) => {
			const item = read(data)
			const title = chatName(item)
			return title === "" || item.n === undefined
				? ""
				: t("aichat.activity.ready.sentence", { n: item.n, title })
		},
		href: (data) => changesHref(read(data)),
		desktop: banner(ActivityReady),
	},
	/* A revision's checks failed, or the published code moved under the same lines. */
	[ActivityAttention]: {
		line: (data) => {
			const item = read(data)
			const title = chatName(item)
			if (title === "") return ""
			if (item.reason === WaitChecksFailed && item.n !== undefined) {
				return t("aichat.activity.attention.checks_failed", { n: item.n, title })
			}
			if (item.reason === WaitNeedsIntegration) {
				return t("aichat.activity.attention.needs_integration", { title })
			}
			return ""
		},
		href: (data) => chatHref(read(data)),
	},
	/* Somebody asks a developer who owns the chat to look at its changes. */
	[ActivityReviewRequested]: {
		line: (data, actorIds, nameOf) => {
			const title = chatName(read(data))
			const name = actor(actorIds, nameOf)
			return title === "" || name === ""
				? ""
				: t("aichat.activity.review_requested.sentence", { name, title })
		},
		href: (data) => changesHref(read(data)),
		desktop: banner(ActivityReviewRequested),
	},
	/* A landing published every repository it held; the row names the parts whose main moved. */
	[ActivityPublished]: {
		line: (data, actorIds, nameOf) => {
			const item = read(data)
			const title = chatName(item)
			const name = actor(actorIds, nameOf)
			if (title === "" || name === "") return ""
			const parts = partNames(item.parts)
			return parts === ""
				? t("aichat.activity.published.sentence", { name, title })
				: t("aichat.activity.published.parts", { name, title, parts })
		},
		href: (data) => changesHref(read(data)),
	},
	/* Publishing stopped part-way; after a day the sweep raises it again, and then it banners again. */
	[ActivityPartlyPublished]: {
		line: (data) => {
			const title = chatName(read(data))
			return title === "" ? "" : t("aichat.activity.partly_published.sentence", { title })
		},
		href: (data) => changesHref(read(data)),
		desktop: banner(ActivityPartlyPublished, labelOf(ActivityPartlyPublished), true),
	},
	/* Somebody else assigned the reader to a chat. */
	[ActivityAssigned]: {
		line: (data, actorIds, nameOf) => {
			const title = chatName(read(data))
			const name = actor(actorIds, nameOf)
			return title === "" || name === ""
				? ""
				: t("aichat.activity.assigned.sentence", { name, title })
		},
		href: (data) => chatHref(read(data)),
		desktop: banner(ActivityAssigned),
	},
	/* Somebody asks to open the chat's private preview (R1b). */
	[ActivityAccess]: {
		line: (data, actorIds, nameOf) => {
			const title = chatName(read(data))
			const name = actor(actorIds, nameOf)
			return title === "" || name === ""
				? ""
				: t("aichat.activity.access.sentence", { name, title })
		},
		href: (data) => chatHref(read(data), { panel: "access" }),
		desktop: banner(ActivityAccess),
	},
	/* An owner mentioned the reader in a prompt or a note. */
	[ActivityMention]: {
		line: (data, actorIds, nameOf) => {
			const title = chatName(read(data))
			const name = actor(actorIds, nameOf)
			return title === "" || name === ""
				? ""
				: t("aichat.activity.mention.sentence", { name, title })
		},
		href: (data) => chatHref(read(data)),
		desktop: banner(ActivityMention),
	},
	/* Thirty quiet days over unpublished changes: is the chat still needed? */
	[ActivityStale]: {
		line: (data) => {
			const title = chatName(read(data))
			return title === "" ? "" : t("aichat.activity.stale.sentence", { title })
		},
		href: (data) => chatHref(read(data)),
	},
}

/**
 * The rows as the feed reads them: every kind names itself by its label unless its entry says more
 * per row, and stands under Claude's mark wherever it names no person.
 */
export const aichatActivityRows = Object.fromEntries(
	AICHAT_ACTIVITY_KINDS.map((kind) => [
		kind,
		{ label: labelOf(kind), face: CLAUDE_FACE, ...ROWS[kind] } satisfies ActivityRowView,
	]),
) as Record<AichatActivityKind, ActivityRowView>
